import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Info, Clock, IndianRupee, Lightbulb, AlertTriangle, ListOrdered } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "bni-md-chat-session";
const SUGGESTIONS = ["What's the agenda?", "How much is a member pass?", "How do I register?", "Where is the venue?", "How does the one-to-one conclave work?"];
const TAGS = {
  INFO:  { cls: "cb-info",  Icon: Info },
  TIME:  { cls: "cb-time",  Icon: Clock },
  PRICE: { cls: "cb-price", Icon: IndianRupee },
  TIP:   { cls: "cb-tip",   Icon: Lightbulb },
  WARN:  { cls: "cb-warn",  Icon: AlertTriangle },
  STEP:  { cls: "cb-step",  Icon: ListOrdered },
};

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = "cs_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(SESSION_KEY, s); }
  return s;
}

function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|₹[\d,]+(?:\.\d+)?)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("₹")) return <span key={i} className="cb-money">{p}</span>;
    return p;
  });
}

export function ChatMarkdown({ text }) {
  const lines = text.split("\n");
  const out = [];
  let stepNo = 0;
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const tag = line.match(/^\[(INFO|TIME|PRICE|TIP|WARN|STEP)\]\s*(.*)$/);
    if (tag) {
      const { cls, Icon } = TAGS[tag[1]];
      if (tag[1] === "STEP") stepNo += 1;
      out.push(
        <div key={i} className={`cb-callout ${cls}`}>
          <span className="cb-callout-ic">{tag[1] === "STEP" ? stepNo : <Icon size={13} />}</span>
          <span><Inline text={tag[2].replace(/^\d+[.)]\s*/, "")} /></span>
        </div>
      );
      return;
    }
    stepNo = 0;
    if (line.startsWith("## ")) { out.push(<div key={i} className="cb-h">{line.slice(3)}</div>); return; }
    if (/^[-•]\s/.test(line)) { out.push(<div key={i} className="cb-li"><span className="cb-dot" /><span><Inline text={line.slice(2)} /></span></div>); return; }
    if (/^\d+\.\s/.test(line)) { out.push(<div key={i} className="cb-li"><span className="cb-num">{line.match(/^\d+/)[0]}</span><span><Inline text={line.replace(/^\d+\.\s/, "")} /></span></div>); return; }
    out.push(<p key={i} className="cb-p"><Inline text={line} /></p>);
  });
  return <div className="cb-md">{out}</div>;
}

export function EventChatbot() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [drift, setDrift] = useState(0);
  const listRef = useRef(null);
  const hidden = pathname === "/big-screen" || pathname.startsWith("/recap/") || pathname.startsWith("/big-screen/");

  useEffect(() => {
    let lastY = window.scrollY, timer;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      if (Math.abs(dy) > 1) setDrift((d) => Math.max(-22, Math.min(22, d * 0.6 + dy * 1.2)));
      clearTimeout(timer);
      timer = setTimeout(() => setDrift(0), 140);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (!open || msgs.length) return;
    fetch(`${API}/api/chat/history/${getSession()}`).then((r) => r.json()).then((d) => setMsgs(d.messages || [])).catch(() => {});
  }, [open]); // eslint-disable-line

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: getSession(), message: q }) });
      if (!res.ok) throw new Error(res.status === 429 ? "You're sending messages too fast — please wait a moment." : "Chat unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const d = JSON.parse(ev.slice(6));
          if (d.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d.delta }; return c; });
          if (d.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `[WARN] ${d.error}` }; return c; });
        }
      }
    } catch (e) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `[WARN] ${e.message}` }; return c; });
    } finally { setBusy(false); }
  };

  if (hidden) return null;
  return (
    <>
      <button data-testid="chatbot-toggle" className={`cb-fab ${open ? "open" : ""}`} style={{ "--drift": `${open ? 0 : drift}px` }} onClick={() => setOpen((o) => !o)} aria-label="Ask about the event">
        {open ? <X size={20} /> : <MessageCircle size={22} />}
        {!open && <span className="cb-fab-label">Ask</span>}
      </button>
      {open && <div className="cb-backdrop" data-testid="chatbot-backdrop" onClick={() => setOpen(false)} />}
      {open && (
        <div className="cb-panel" data-testid="chatbot-panel" role="dialog" aria-label="Event assistant">
          <div className="cb-head">
            <span className="cb-head-ic"><Sparkles size={15} /></span>
            <div><div className="cb-title">RISSE Concierge</div><div className="cb-sub">Members Day · 28 Nov 2026</div></div>
            <button data-testid="chatbot-close" className="cb-x" onClick={() => setOpen(false)} aria-label="Close"><X size={16} /></button>
          </div>
          <div className="cb-list" ref={listRef} data-testid="chatbot-messages">
            {msgs.length === 0 && (
              <div className="cb-welcome">
                <p className="cb-p">Hi! Ask me anything about Members Day — agenda, passes, venue, registration, one-to-ones, or the photo gallery.</p>
                <div className="cb-chips">
                  {SUGGESTIONS.map((s) => <button key={s} data-testid="chatbot-suggestion" className="cb-chip" onClick={() => send(s)}>{s}</button>)}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`cb-msg ${m.role}`} data-testid={`chatbot-msg-${m.role}`}>
                {m.role === "assistant"
                  ? (m.content ? <ChatMarkdown text={m.content} /> : <span className="cb-typing"><i /><i /><i /></span>)
                  : m.content}
              </div>
            ))}
          </div>
          <form className="cb-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input data-testid="chatbot-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about the event…" maxLength={1000} disabled={busy} />
            <button data-testid="chatbot-send" type="submit" disabled={busy || !input.trim()} aria-label="Send"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
}
